"use client";

import Image from "@/app/components/ProtectedImage";
import Link from "next/link";
import { useLocale, useTranslations } from "@/i18n/TranslationProvider";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import type { CarBrand } from "@/types";

type CarBrandCarouselProps = {
  initialShowAll?: boolean;
  hideToggle?: boolean;
  logosOnly?: boolean;
  brands: CarBrand[];
};

const AUTO_ADVANCE_MS = 7000;
// After a visitor pages by hand, wait this long before auto-advancing again.
const MANUAL_PAUSE_MS = 15000;

function debounce<T extends (...args: unknown[]) => void>(
  fn: T,
  delay: number,
): T {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return ((...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  }) as T;
}

export default function CarBrandCarousel({
  initialShowAll = false,
  hideToggle = false,
  logosOnly = false,
  brands,
}: CarBrandCarouselProps) {
  const t = useTranslations();
  const locale = useLocale();
  const [currentCarSlide, setCurrentCarSlide] = useState(0);
  const [showAllBrands, setShowAllBrands] = useState(initialShowAll);
  const [itemsPerSlide, setItemsPerSlide] = useState(8);
  const [lastManualAt, setLastManualAt] = useState(0);
  const resizeRef = useRef<ReturnType<typeof debounce> | null>(null);

  const totalSlides = Math.ceil(brands.length / itemsPerSlide);

  const currentSlideItems = useMemo(() => {
    if (showAllBrands) return brands;
    const start = currentCarSlide * itemsPerSlide;
    return brands.slice(start, start + itemsPerSlide);
  }, [brands, currentCarSlide, itemsPerSlide, showAllBrands]);

  const nextCarSlide = useCallback(() => {
    setLastManualAt(Date.now());
    setCurrentCarSlide((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevCarSlide = useCallback(() => {
    setLastManualAt(Date.now());
    setCurrentCarSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  const goToSlide = useCallback((index: number) => {
    setLastManualAt(Date.now());
    setCurrentCarSlide(index);
  }, []);

  const toggleShowAllBrands = useCallback(() => {
    setShowAllBrands((prev) => !prev);
  }, []);

  useEffect(() => {
    const compute = () => {
      const width = window.innerWidth;
      const per = width < 640 ? 4 : width < 1024 ? 6 : 8;
      setItemsPerSlide(per);
      const newTotal = Math.ceil(brands.length / per);
      setCurrentCarSlide((prev) => Math.min(prev, Math.max(newTotal - 1, 0)));
    };

    compute();

    resizeRef.current = debounce(compute, 200);

    const handleResize = () => resizeRef.current?.();
    window.addEventListener("resize", handleResize, { passive: true });

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [brands.length]);

  // One timer per slide, restarted on every change, so a manual click never meets an
  // auto-advance a moment later.
  useEffect(() => {
    if (showAllBrands || totalSlides <= 1) return;
    const delay = Math.max(AUTO_ADVANCE_MS, lastManualAt + MANUAL_PAUSE_MS - Date.now());
    const timer = setTimeout(() => {
      setCurrentCarSlide((prev) => (prev + 1) % totalSlides);
    }, delay);
    return () => clearTimeout(timer);
  }, [currentCarSlide, lastManualAt, totalSlides, showAllBrands]);

  return (
    <section className="py-8 md:py-16 bg-gray-50 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 md:mb-12 flex flex-col items-center md:flex-row md:items-end md:justify-between">
          <h2 className="text-2xl md:text-4xl font-bold text-black mb-4 md:mb-0 drop-shadow-sm text-center md:text-left">
            {t.brands.title}
          </h2>
          {!hideToggle && (
            <button
              onClick={toggleShowAllBrands}
              className="inline-flex items-center px-3 py-1.5 md:px-4 md:py-2 rounded-full text-sm md:text-base border border-black text-black hover:bg-black hover:text-white transition-colors"
              aria-label={showAllBrands ? t.brands.showLess : t.brands.viewAll}
            >
              {showAllBrands ? t.brands.showLess : t.brands.viewAll}
            </button>
          )}
        </div>

        <div className="relative px-2 md:px-0">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6 grid-rows-2">
            {currentSlideItems.map((brand, index) => (
              <Link
                key={`${currentCarSlide}-${brand.slug}-${index}`}
                href={`/${locale}/brand/${brand.slug}`}
                className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group relative overflow-hidden border hover:border-gray-300"
              >
                <div className="relative h-28 sm:h-36 md:h-48 rounded-lg overflow-hidden">
                  <Image
                    src={logosOnly ? brand.logo : brand.image}
                    alt={logosOnly ? `${brand.name} logo` : brand.name}
                    fill
                    className={`object-contain ${
                      logosOnly
                        ? "p-6 sm:p-8 md:p-10"
                        : "p-4 group-hover:scale-110 transition-transform duration-300"
                    }`}
                    loading={index < 4 ? "eager" : "lazy"}
                    sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                  />

                  {!logosOnly && (
                    <div className="absolute inset-0 bg-white bg-opacity-95 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <div className="relative w-28 h-20 transform group-hover:scale-110 transition-transform duration-300">
                        <Image
                          src={brand.logo}
                          alt={`${brand.name} logo`}
                          fill
                          sizes="112px"
                          className="object-contain drop-shadow-md"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-2 md:p-4 text-center">
                  <h3 className="text-sm md:text-base font-bold text-gray-900 group-hover:text-black transition-colors duration-300">
                    {brand.name}
                  </h3>
                </div>
              </Link>
            ))}
          </div>

          {!showAllBrands && totalSlides > 1 && (
            <div className="flex items-center justify-between mt-4 md:mt-8">
              <button
                onClick={prevCarSlide}
                className="bg-black hover:bg-gray-800 text-white w-10 h-8 md:w-12 md:h-10 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 touch-manipulation flex items-center justify-center"
                aria-label="Previous car brands"
              >
                <svg
                  className="w-5 h-5 md:w-8 md:h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>

              <div className="flex justify-center space-x-2 md:space-x-3">
                {Array.from({ length: totalSlides }, (_, index) => (
                  <button
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-3 h-3 md:w-4 md:h-4 rounded-full transition-all duration-300 border-2 border-black touch-manipulation ${
                      index === currentCarSlide
                        ? "bg-black scale-110 shadow-lg"
                        : "bg-white hover:bg-gray-200 hover:scale-105"
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={nextCarSlide}
                className="bg-black hover:bg-gray-800 text-white w-10 h-8 md:w-12 md:h-10 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 touch-manipulation flex items-center justify-center"
                aria-label="Next car brands"
              >
                <svg
                  className="w-5 h-5 md:w-8 md:h-8"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
